import { Close, InfoOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Divider,
  Switch,
  Tooltip,
  ListItem,
  List,
} from '@mui/material';
import {
  GenButton,
  GenCurrency,
  GenDate,
} from '@pinnacleautomotive/clarity-components';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  collectIds,
  setRoLabels,
  sumAmount,
} from '../../../helpers/repairOrders';

export default function EditRo({
  open,
  title,
  onClose,
  orderDetails,
  loading,
  onSave,
  setRoNumber,
  ro,
  checked,
  packageTotalValue,
}) {
  const { t } = useTranslation(['orders', 'global']);
  const { register, reset, handleSubmit, watch } = useForm({
    mode: 'onChange',
  });

  const onCloseHandle = () => {
    reset({ ...orderDetails });
    onClose();
    setRoNumber({
      open: false,
      roNumber: null,
      checked: false,
      packageValue: null,
    });
  };

  useEffect(() => {
    if (loading) return;
    reset({ ...orderDetails });
  }, [reset, orderDetails, loading]);

  const getServicesName = orders => {
    const serviceNames = new Set();
    orders.forEach(order => {
      order.services.forEach(service => {
        serviceNames.add(service.name);
      });
    });
    return [...serviceNames].map(name => (
      <ListItem
        sx={{
          padding: 0,
          listStyleType: 'disc',
          display: 'list-item',
          marginLeft: 1,
        }}
      >
        {name}
      </ListItem>
    ));
  };

  const allFieldValues = watch();
  const isSubmitDisabled = !Object.values(allFieldValues).some(
    value => typeof value === 'string' && value.trim() !== ''
  );

  const DialogTitleChildren = () => (
    <Box display="flex" flexDirection="row" alignItems="center">
      <Typography variant="subtitle2" flexGrow={1}>
        {title}
      </Typography>
      <IconButton
        aria-label="close"
        onClick={onCloseHandle}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500],
        }}
      >
        <Close />
      </IconButton>
    </Box>
  );
  return (
    <Dialog open={open} maxWidth="xs" onClose={onCloseHandle}>
      <DialogTitle sx={{ background: theme => theme.palette.grey[50] }}>
        <DialogTitleChildren />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} pt={2} alignItems="center">
          <Grid item xs={10}>
            <Typography variant="body1" flexGrow={1}>
              {t('editRo.applyToAll')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Switch
              checked={checked}
              onChange={e =>
                setRoNumber({
                  checked: e.currentTarget.checked,
                  open: true,
                  roNumber: ro,
                })
              }
            />
          </Grid>
          {checked && (
            <Grid item xs={12}>
              <TextField
                label={'RO#'}
                fullWidth
                size="small"
                defaultValue={ro}
                onChange={e =>
                  setRoNumber({
                    roNumber: e.target.value,
                    open: true,
                    checked: checked,
                  })
                }
                {...register('oneRo', { required: false })}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {Object.entries(orderDetails).map(([type, detail]) => {
            return (
              <>
                {Object.entries(detail).map(([date, order], index) => {
                  return (
                    <>
                      <Grid item xs={6}>
                        <Typography
                          variant="body1"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {type} {index + 1}
                          <Tooltip
                            arrow
                            title={
                              <List sx={{ listStyleType: 'disc' }}>
                                {getServicesName(order)}
                              </List>
                            }
                          >
                            <IconButton>
                              <InfoOutlined
                                sx={{ color: 'rgba(0, 0, 0, 0.54)' }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <Typography variant="body2">
                          <GenCurrency
                            value={
                              type === 'packages'
                                ? packageTotalValue
                                : sumAmount(order)
                            }
                          />{' '}
                          · <GenDate date={date} noTime={true} />
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label={setRoLabels(checked, ro, order, 'RO#')}
                          fullWidth
                          size="small"
                          disabled={checked}
                          {...register(`${collectIds(order)}`, {
                            required: false,
                          })}
                        />
                      </Grid>
                    </>
                  );
                })}
              </>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap={2}
          p={2}
        >
          <GenButton
            text={t('global:genericButtons.cancel')}
            order={0}
            disabled={loading}
            onClick={onCloseHandle}
            sx={{
              color: theme => theme.palette.text.primary,
              borderColor: theme => theme.palette.divider,
              width: '104px',
              height: '36px',
            }}
          />
          <LoadingButton
            color="primary"
            variant="contained"
            loading={loading}
            onClick={handleSubmit(onSave)}
            disabled={checked ? !watch('oneRo') : isSubmitDisabled}
            sx={{
              width: '159px',
              height: '36px',
            }}
          >
            {t('global:genericButtons.saveChanges')}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
